import LocomotiveScroll from 'locomotive-scroll';

import breakpoints from './utils/breakpoints.js';
import { BrowserDetect, OsDetect, browserResults, isIE, isEdge, osResults } from './utils/browser-detect.js';
import { nodeEach, delegateEvent, getOffset, registerGlobals } from './utils/dom-helpers.js';
import { initAnchorScroll, getAnchorScrollOffset } from './utils/anchor-scroll.js';
import loadSvgSprite from './utils/sprite-loader.js';

import Navigation from './components/navigation.js';
import SearchToggle from './components/search-toggle.js';
import SearchForm from './components/search-form.js';
import MenuToggle from './components/menu-toggle.js';
import VideoInView from './components/video-in-view.js';
import { initReactApps } from './components/react-loader.js';

const FONTS_LOADED_CLASS = 'fonts-loaded';
const IS_LOADED_CLASS = 'is-loaded';

// --- 1. HTML class setup ---
(function () {
  const html = document.documentElement;
  html.classList.remove('no-js');
  html.classList.add('has-js');
  BrowserDetect.addClassNames(browserResults);
  OsDetect.addClassNames(osResults);
  if (isIE) html.classList.add(`is-msie-${BrowserDetect.getVersionOfIE(navigator)}`);
  if (isEdge) html.classList.add(`is-msie-${BrowserDetect.getVersionOfEdge(navigator)}`);
})();

// --- 2. Breakpoint change listener ---
(function () {
  let currentBp = breakpoints.getBreakpointByValue(window.innerWidth);
  window.addEventListener(
    'resize',
    throttle(150, () => {
      const newBp = breakpoints.getBreakpointByValue(window.innerWidth);
      if (newBp.name === currentBp.name) return;
      const event = new CustomEvent('breakpointchange', {
        detail: { breakpoints, currentBreakpoint: newBp },
      });
      currentBp = newBp;
      window.dispatchEvent(event);
    })
  );
})();

// --- 3. Load SVG sprite ---
loadSvgSprite();

// --- 4. Reduced motion ---
window.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// --- 5. Locomotive Scroll (wraps Lenis) ---
new LocomotiveScroll({
  scrollCallback: function ({ scroll }) {
    const cls = 'has-scrolled';
    if (scroll > 24 && !document.documentElement.classList.contains(cls)) {
      document.documentElement.classList.add(cls);
    }
    if (scroll <= 24 && document.documentElement.classList.contains(cls)) {
      document.documentElement.classList.remove(cls);
    }
  },
  lenisOptions: {
    lerp: 0.2,
    smoothWheel: !window.isReducedMotion,
  },
});

// --- 6. Viewport width CSS variable ---
function updateVw() {
  const vw = 0.01 * document.body.clientWidth;
  document.documentElement.style.setProperty('--vw', `${vw}px`);
}
updateVw();
window.addEventListener('resize', updateVw);

// --- 7. Anchor scroll ---
initAnchorScroll();

// --- 8. Language check ---
const currentLang = document.documentElement.lang.slice(0, 2);
const browserLang = navigator.language.slice(0, 2);
const supportedLanguages = ['de', 'en', 'fr', 'it', 'es'];
const COOKIE_NAME = 'languagePreference';

if (!document.cookie.includes(COOKIE_NAME) && currentLang !== browserLang && supportedLanguages.includes(browserLang)) {
  import('./components/language-dialog.js').then(({ default: LanguageDialog }) => {
    new LanguageDialog({
      currentLanguage: currentLang,
      browserLanguage: browserLang,
      supportedLanguages,
      cookieName: COOKIE_NAME,
    }).init();
  });
}

const langToggles = document.querySelectorAll('[data-language-toggle]');
if (langToggles.length > 0) {
  import('./components/language-toggle.js').then(({ default: LanguageToggle }) => {
    nodeEach(langToggles, (el) => new LanguageToggle(el, COOKIE_NAME).init());
  });
}

// --- 9. Component auto-init ---
(function () {
  // Select
  const selects = document.querySelectorAll('[data-select]');
  if (selects.length > 0) {
    import('./components/select.js').then(({ default: Select }) => {
      nodeEach(selects, (el) => new Select(el).init());
    });
  }

  // Uploads
  const uploads = document.querySelectorAll('[data-uploads]');
  if (uploads.length > 0) {
    import('./components/uploads.js').then(({ default: Uploads }) => {
      nodeEach(uploads, (el) => new Uploads(el).init());
    });
  }

  // Form
  const forms = document.querySelectorAll('[data-form-container]');
  if (forms.length > 0) {
    import('./components/form.js').then(({ default: Form }) => {
      nodeEach(forms, (el) => new Form(el).init());
    });
  }

  // Video dialog
  const videos = document.querySelectorAll('[data-video]');
  if (videos.length > 0) {
    import('./components/video-dialog.js').then(({ default: VideoDialog }) => {
      nodeEach(videos, (el) => new VideoDialog(el).init());
    });
  }

  // Accordion
  const accordions = document.querySelectorAll('[data-accordion]');
  if (accordions.length > 0) {
    import('./components/accordion.js').then(({ default: Accordion }) => {
      nodeEach(accordions, (el) => new Accordion(el).init());
    });
  }

  // Slider: Teaser
  const sliderTeasers = document.querySelectorAll('[data-slider-teaser]');
  if (sliderTeasers.length > 0) {
    import('./sliders/slider-teaser.js').then(({ default: SliderTeaser }) => {
      nodeEach(sliderTeasers, (el) => new SliderTeaser(el).init());
    });
  }

  // Slider: Products
  const sliderProducts = document.querySelectorAll('[data-slider-products]');
  if (sliderProducts.length > 0) {
    import('./sliders/slider-products.js').then(({ default: SliderProducts }) => {
      nodeEach(sliderProducts, (el) => new SliderProducts(el).init());
    });
  }

  // Marquee
  const marquees = document.querySelectorAll('[data-logo-marquee]');
  if (marquees.length > 0) {
    import('./components/marquee.js').then(({ default: LogoMarquee }) => {
      nodeEach(marquees, (el) => new LogoMarquee(el).init());
    });
  }

  // Tabs
  const tabs = document.querySelectorAll('[data-tabs]');
  if (tabs.length > 0) {
    import('./components/tabs.js').then(({ default: Tabs }) => {
      nodeEach(tabs, (el) => new Tabs(el).init());
    });
  }

  // Nav on-page
  const navOnpage = document.querySelectorAll('[data-nav-onpage-container]');
  if (navOnpage.length > 0) {
    import('./components/nav-onpage.js').then(({ default: NavOnpage }) => {
      nodeEach(navOnpage, (el) => new NavOnpage(el).init());
    });
  }

  // Slider: Testimonial
  const sliderTestimonials = document.querySelectorAll('[data-slider-testimonial]');
  if (sliderTestimonials.length > 0) {
    import('./sliders/slider-testimonial.js').then(({ default: SliderTestimonial }) => {
      nodeEach(sliderTestimonials, (el) => new SliderTestimonial(el).init());
    });
  }

  // Breadcrumb
  const breadcrumbs = document.querySelectorAll('[data-breadcrumb]');
  if (breadcrumbs.length > 0) {
    import('./components/breadcrumb.js').then(({ default: Breadcrumb }) => {
      nodeEach(breadcrumbs, (el) => new Breadcrumb(el).init());
    });
  }

  // Video in view
  const videosInView = document.querySelectorAll('[data-video-in-view]');
  if (videosInView.length > 0) {
    nodeEach(videosInView, (el) => new VideoInView(el).init());
  }

  // Video hero pause
  const videoHeroes = document.querySelectorAll('[data-pause-video-hero]');
  if (videoHeroes.length > 0) {
    import('./components/video-hero.js').then(({ default: VideoHero }) => {
      nodeEach(videoHeroes, (el) => new VideoHero(el).init());
    });
  }

  // Slider: Media
  const sliderMedia = document.querySelectorAll('[data-slider-media]');
  if (sliderMedia.length > 0) {
    import('./sliders/slider-media.js').then(({ default: SliderMedia }) => {
      nodeEach(sliderMedia, (el) => new SliderMedia(el).init());
    });
  }

  // Dialog (media lightbox)
  const dialogs = document.querySelectorAll('[data-dialog]');
  if (dialogs.length > 0) {
    import('./components/media-dialog.js').then(({ default: MediaDialog }) => {
      nodeEach(dialogs, (el) => new MediaDialog(el).init());
    });
  }

  // Product detail
  const productDetails = document.querySelectorAll('[data-product-detail]');
  if (productDetails.length > 0) {
    import('./sliders/slider-product-detail.js').then(({ default: SliderProductDetail }) => {
      nodeEach(productDetails, (el) => new SliderProductDetail(el).init());
    });
  }

  // Table
  const tables = document.querySelectorAll('[data-table-container]');
  if (tables.length > 0) {
    import('./components/table.js').then(({ default: Table }) => {
      nodeEach(tables, (el) => new Table(el).init());
    });
  }

  // Search partner
  const searchPartners = document.querySelectorAll('[data-search-partner]');
  if (searchPartners.length > 0) {
    import('./components/search-partner.js').then(({ default: SearchPartner }) => {
      nodeEach(searchPartners, (el) => new SearchPartner(el).init());
    });
  }

  // Slider: Services
  const sliderServices = document.querySelectorAll('[data-slider-services]');
  if (sliderServices.length > 0) {
    import('./sliders/slider-services.js').then(({ default: SliderServices }) => {
      nodeEach(sliderServices, (el) => new SliderServices(el).init());
    });
  }

  // Navigation (inline, not lazy-loaded)
  const navs = document.querySelectorAll('[data-nav]');
  if (navs.length > 0) {
    nodeEach(navs, (el) => new Navigation(el).init());
  }

  // Search toggle (inline)
  const searchToggles = document.querySelectorAll('[data-search-toggle]');
  if (searchToggles.length > 0) {
    nodeEach(searchToggles, (el) => new SearchToggle(el).init());
  }

  // Search form (inline)
  const searchForms = document.querySelectorAll('[data-search-form]');
  if (searchForms.length > 0) {
    nodeEach(searchForms, (el) => new SearchForm(el).init());
  }

  // Menu toggle (inline)
  const menuToggles = document.querySelectorAll('[data-menu-toggle]');
  if (menuToggles.length > 0) {
    nodeEach(menuToggles, (el) => new MenuToggle(el).init());
  }

  // Portal dialog
  const portalDialogs = document.querySelectorAll('[data-portal-dialog]');
  if (portalDialogs.length > 0) {
    import('./components/portal-dialog.js').then(({ default: PortalDialog }) => {
      nodeEach(portalDialogs, (el) => new PortalDialog(el).init());
    });
  }

  // XHR Search
  const xhrSearch = document.querySelector('.js--xhr-search');
  if (xhrSearch) {
    import('./components/xhr-search.js').then(({ default: XhrSearch }) => {
      new XhrSearch(xhrSearch).init();
    });
  }

  // Slider: Process
  const sliderProcess = document.querySelectorAll('[data-slider-process]');
  if (sliderProcess.length > 0) {
    import('./sliders/slider-process.js').then(({ default: SliderProcess }) => {
      nodeEach(sliderProcess, (el) => new SliderProcess(el).init());
    });
  }

  // Tooltip
  const tooltips = document.querySelectorAll('[data-tooltip]');
  if (tooltips.length > 0) {
    import('./components/tooltip.js').then(({ default: Tooltip }) => {
      nodeEach(tooltips, (el) => new Tooltip(el).init());
    });
  }

  // Process accordion
  const processAccordions = document.querySelectorAll('[data-process-accordion]');
  if (processAccordions.length > 0) {
    import('./components/process-accordion.js').then(({ default: ProcessAccordion }) => {
      nodeEach(processAccordions, (el) => new ProcessAccordion(el).init());
    });
  }
})();

// --- 10. React apps ---
initReactApps();

// --- 11. Font loading ---
if (sessionStorage.fontsLoaded) {
  document.documentElement.classList.add(FONTS_LOADED_CLASS);
} else if ('fonts' in document && typeof document.fonts.load === 'function') {
  Promise.all([
    document.fonts.load('500 1em Pangea'),
    document.fonts.load('400 1em Pangea Text'),
    document.fonts.load('500 1em Pangea Text'),
  ])
    .then(() => {
      document.documentElement.classList.add(FONTS_LOADED_CLASS);
      sessionStorage.fontsLoaded = true;
    })
    .catch(() => {
      console.error('Fonts failed to load.');
    });
} else {
  document.documentElement.classList.add(FONTS_LOADED_CLASS);
  sessionStorage.fontsLoaded = true;
}

// --- 12. CSS loaded detection ---
window.addEventListener('load', () => {
  const mainCss = document.getElementById('main-css');
  if (!mainCss) {
    // Vite dev mode injects CSS via JS — no <link> tag exists, so mark as loaded directly
    document.documentElement.classList.add(IS_LOADED_CLASS);
    return;
  }
  if (mainCss.isLoaded) {
    document.documentElement.classList.add(IS_LOADED_CLASS);
  } else {
    mainCss.addEventListener('load', () => {
      document.documentElement.classList.add(IS_LOADED_CLASS);
    });
  }
});

// --- 13. Register global helpers ---
registerGlobals();

// --- Throttle utility (inline, used for breakpoint resize) ---
function throttle(delay, callback) {
  let lastCall = 0;
  let timeout;
  return function (...args) {
    const now = Date.now();
    const remaining = delay - (now - lastCall);
    clearTimeout(timeout);
    if (remaining <= 0) {
      lastCall = now;
      callback.apply(this, args);
    } else {
      timeout = setTimeout(() => {
        lastCall = Date.now();
        callback.apply(this, args);
      }, remaining);
    }
  };
}
